/// <amd-module name="Build/Admin/Agent/Components/AgentJobsContainer" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";

import { TaskAgentJobRequest } from "DistributedTask/Client/Contracts/TaskAgent";

import { ITeamProject } from "TFS/Services/TfsProjectService";

import { AgentActionCreator } from "Build/Admin/Agent/Actions/AgentActionCreator";
import { StoresHub } from "Build/Admin/Agent/Stores/StoresHub";
import { JobsFilterBar } from "Build/Admin/Common/Components/JobsFilterBar";
import { JobsList, AgentColumnStyle } from "Build/Admin/Common/Components/JobsList";
import { Assets } from "Build/Admin/Common/Constants";
import { SignalRState } from "Build/Admin/Common/Stores/SignalRStore";

import * as Resources from "Build/Admin/Agent/Resources";

export interface IAgentJobsContainerProps {
    actionCreator: AgentActionCreator;
    storesHub: StoresHub;
    filterToggled: ObservableValue<boolean>;
    onFilterToggled(): void;
}

export interface IAgentJobsContainerState {
    jobs: TaskAgentJobRequest[];
    projects: ITeamProject[];
    signalRState: SignalRState;
    renderZeroData: boolean;
}

export class AgentJobsContainer extends VssComponent<IAgentJobsContainerProps, IAgentJobsContainerState> {
    constructor(props: IAgentJobsContainerProps) {
        super(props);
        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
        this.props.storesHub.jobStore.addChangedListener(this._onChanged);
        this.props.storesHub.projectStore.addChangedListener(this._onChanged);
        this.props.storesHub.signalRStore.addChangedListener(this._onChanged);
        this.props.storesHub.jobStore.filter.subscribe(this._onChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
        this.props.storesHub.jobStore.removeChangedListener(this._onChanged);
        this.props.storesHub.projectStore.removeChangedListener(this._onChanged);
        this.props.storesHub.signalRStore.removeChangedListener(this._onChanged);
        this.props.storesHub.jobStore.filter.unsubscribe(this._onChanged, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        return (
            <>
                <ConditionalChildren renderChildren={!this.state.renderZeroData && this.props.filterToggled}>
                    <div className="page-content-top page-content-left page-content-right">
                        {this._renderFilterBar()}
                    </div>
                </ConditionalChildren>
                <div className="pipelines-pool-jobs page-content page-content-top">
                    {
                        this.state.renderZeroData &&
                        <ZeroData
                            primaryText={Resources.ZeroAgentJobsPrimaryText}
                            imagePath={getAssetLocation(this.context.pageContext, Assets.ZeroDataPng)}
                            imageAltText="" />
                    }
                    {
                        !this.state.renderZeroData &&
                        <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                            <JobsList
                                agentColumnStyle={AgentColumnStyle.None}
                                jobs={this.state.jobs}
                                projects={this.state.projects}
                                signalRState={this.state.signalRState}
                            />
                        </Card>
                    }
                </div>
            </>
        );
    }

    private _renderFilterBar = (): JSX.Element => {
        return <JobsFilterBar
            agentColumnStyle={AgentColumnStyle.None}
            agents={[]}
            agentDefinitions={[]}
            projects={this.state.projects}
            filter={this.props.storesHub.jobStore.filter}
            onFilterToggled={this.props.onFilterToggled}
        />;
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IAgentJobsContainerState {
        const projects: ITeamProject[] = this.props.storesHub.projectStore.state;
        return {
            jobs: this.props.storesHub.jobStore.getFilteredJobs(projects, false),
            projects,
            signalRState: this.props.storesHub.signalRStore.state,
            renderZeroData: this.props.storesHub.jobStore.state.length === 0
        };
    }
}
